import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, useTheme } from "@mui/material";
import IconArrowBack from "@mui/icons-material/ArrowBack";
import IconArrowForward from "@mui/icons-material/ArrowForward";
import { STEP_IDS } from "consts";
import { getStepId } from "utils/helpers";
import getStyles from "./styles";

const stepIds = [...STEP_IDS];

interface IProps {
  previous?: string;
  next?: string;
  setCurrentStep: (step: number) => void;
  validationCallback: (targetIndex: number) => boolean;
}

const Navigation: React.FC<IProps> = ({ previous, next, setCurrentStep, validationCallback }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handlePrevious = useCallback(() => {
    if (!previous) return;

    setCurrentStep(getStepId(stepIds, previous));
    window.scrollTo(0, 0);
  }, [previous, setCurrentStep]);

  const handleNext = useCallback(() => {
    if (!next) return;

    const targetIndex = stepIds.indexOf(next);
    if (validationCallback(targetIndex)) {
      setCurrentStep(targetIndex + 1);
      window.scrollTo(0, 0);
    }
  }, [next, setCurrentStep, validationCallback]);

  return (
    <Stack component="footer" sx={styles.container} className="RlxNavigation">
      <Box>
        {previous && (
          <Button
            variant="contained"
            startIcon={<IconArrowBack />}
            onClick={handlePrevious}
            sx={styles.button}
          >
            {t(previous)}
          </Button>
        )}
      </Box>
      <Box>
        {next && (
          <Button
            variant="contained"
            endIcon={<IconArrowForward />}
            onClick={handleNext}
            sx={styles.button}
          >
            {t(next)}
          </Button>
        )}
      </Box>
    </Stack>
  );
};

Navigation.displayName = "Navigation";

export default memo(Navigation);
